import { useTranslation } from "react-i18next";
import { type useEventFormDialogContents } from "../../../../hooks";
import {
  Badge,
  Checkbox,
  ColorPicker,
  Combobox,
  DateTimePicker,
  Input,
  Label,
  MultiSelect,
  NumberInput,
  Textarea,
  useDateFnsFormattersWithLocale,
} from "@tellescope/ui";
import { useMemo } from "react";
import { formatName } from "@tellescope/utils";
import { getEventCellColorsFromHex, isUserAvailableForRange } from "../../../../utils";
import { useAuth } from "@tellescope/tanstack-query";

export interface InfoSectionProps
  extends Pick<
    ReturnType<typeof useEventFormDialogContents>,
    | "form"
    | "templates"
    | "templateOptions"
    | "calculatedEndDate"
    | "users"
    | "endusers"
    | "enduserSearch"
    | "setEnduserSearch"
  > {
  timezone?: string;
}

export const InfoSection = ({
  form,
  templates,
  templateOptions,
  calculatedEndDate,
  users,
  endusers,
  enduserSearch,
  setEnduserSearch,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
}: InfoSectionProps) => {
  const { formatInTimeZone } = useDateFnsFormattersWithLocale();
  const { t } = useTranslation("calendar");
  const { selectedOrganization } = useAuth();
  const { value: templateId, onValueChange: onTemplateIdChange } = form.getCustomFieldProps("templateId");
  const selectedTemplateOption = useMemo(
    () => templateOptions.find((option) => option.value === templateId),
    [templateOptions, templateId]
  );

  const colorPreview = getEventCellColorsFromHex(form.values.color);

  const startDate = form.values.startDate;
  const duration = form.values.duration;
  const unavailableUserIds = useMemo(() => {
    if (!startDate || duration === undefined) return new Set<string>();
    return users.reduce<Set<string>>((acc, user) => {
      if (!isUserAvailableForRange(user.weeklyAvailabilities ?? [], startDate, duration, timezone)) {
        acc.add(user.id);
      }
      return acc;
    }, new Set<string>());
  }, [users, startDate, duration, timezone]);

  return (
    <form {...form.getFormProps()} className="space-y-4">
      <div className="space-y-2">
        <Label required htmlFor="event-title">
          {t("Title")}
        </Label>
        <Input
          {...form.getFieldProps("title")}
          data-testid="event-form-title"
          id="event-title"
          autoFocus
          placeholder={t("Event title")}
        />
      </div>
      <div className="space-y-2">
        <Label className="flex gap-1" required={selectedOrganization?.settings?.calendar?.templateRequired}>
          {t("Event Template")}
          {!selectedOrganization?.settings?.calendar?.templateRequired && (
            <span className="text-muted-foreground font-light">{t("(optional)")}</span>
          )}
        </Label>
        <div className="space-y-1">
          <Combobox
            value={selectedTemplateOption}
            onChange={(option) => {
              onTemplateIdChange(option?.value ?? "");
              if (!option?.value) return;
              const template = templates.find(({ id }) => id === option.value);
              form.setFieldValue("title", template?.displayTitle || template?.title || "");
              form.setFieldValue("description", template?.displayDescription || template?.description || "");
              if (template?.durationInMinutes !== undefined) {
                form.setFieldValue("duration", template?.durationInMinutes);
              }
            }}
            options={templateOptions}
            placeholder={t("Select a template...")}
            searchPlaceholder={t("Search templates...")}
            emptyText={t("No templates found")}
            clearable
            aria-label={t("Event Template")}
            formatSelectedValue={(value) => (
              <div className="flex flex-col gap-1 items-start overflow-hidden w-full text-left">
                <div className="font-light text-sm truncate w-full">{value.label}</div>
              </div>
            )}
          />
          <p className="text-xs text-muted-foreground">{t("Title, duration, and description auto-populated.")}</p>
        </div>
      </div>
      <div className="space-y-2">
        <Label htmlFor="event-description">{t("Description")}</Label>
        <Textarea
          {...form.getFieldProps("description")}
          id="event-description"
          className="font-light"
          placeholder={t("Optional notes or description")}
          style={{
            fieldSizing: "content",
          }}
        />
      </div>
      <div className="space-y-2">
        <div className="flex justify-between w-full items-center">
          <Label required htmlFor="event-color" onClick={(e) => e.preventDefault()}>
            {t("Color")}
          </Label>
          <div
            className="text-xs border rounded-sm py-0.5 px-2 text-nowrap flex items-center justify-between gap-2"
            style={{
              background: colorPreview.backgroundColor,
              borderColor: colorPreview.borderColor,
              color: colorPreview.textColor,
            }}
          >
            {t("Color Preview")}
          </div>
        </div>
        <ColorPicker
          {...form.getCustomFieldProps("color")}
          id="event-color"
          className="font-light"
          showName
          placeholder={t("Pick a color")}
        />
      </div>
      <div className="space-y-2">
        <Label required htmlFor="event-start-date" onClick={(e) => e.preventDefault()}>
          {t("Start Date and Time")}
        </Label>
        <DateTimePicker
          {...form.getCustomFieldProps("startDate")}
          id="event-start-date"
          className="font-light"
          label=""
          placeholder={t("Select a date and time")}
          timezone={timezone}
        />
      </div>
      <div className="space-y-2">
        <Label required htmlFor="event-duration">
          {t("Duration (mins)")}
        </Label>
        <div className="space-y-1">
          <NumberInput
            value={form.values.duration}
            onChange={(value) => form.setFieldValue("duration", value)}
            onBlur={() => form.getCustomFieldProps("duration").onBlur()}
            placeholder={t("Enter a duration in minutes")}
            aria-invalid={form.errors.duration ? true : undefined}
            aria-required
            options={[
              {
                value: 15,
                label: t("15 mins"),
              },
              {
                value: 30,
                label: t("30 mins"),
              },
              {
                value: 45,
                label: t("45 mins"),
              },
              {
                value: 60,
                label: t("1 hour"),
              },
              {
                value: 90,
                label: t("1.5 hours"),
              },
              {
                value: 120,
                label: t("2 hours"),
              },
            ]}
          />

          {calculatedEndDate ? (
            <p className="text-xs text-muted-foreground">
              {t("Event will end: {{timestamp}}", {
                timestamp: formatInTimeZone(calculatedEndDate, timezone, "MMM d, yyyy h:mm a zzz"),
              })}
            </p>
          ) : null}
        </div>
      </div>
      <div className="h-[1px] bg-gray-200 w-full" />
      <Label required>{t("Attendees")}</Label>
      <div className="space-y-2">
        <p className="text-muted-foreground text-xs">{t("Users")}</p>
        <MultiSelect
          {...form.getCustomFieldProps("userIds")}
          options={users.map((user) => ({
            value: user.id,
            label: formatName(user) || t("User: {{id}}", { id: user.id }),
          }))}
          renderOption={(option) => (
            <div className="flex items-center justify-between gap-2 w-full">
              <span className="truncate">{option.label}</span>
              {unavailableUserIds.has(option.value) && (
                <Badge variant="outline" className="flex-shrink-0 text-destructive border-destructive">
                  {t("Unavailable")}
                </Badge>
              )}
            </div>
          )}
          clearSearchOnSelect={false}
          placeholder={t("Search users...")}
          className="text-light w-full"
        />
      </div>
      <div className="space-y-2">
        <p className="text-muted-foreground text-xs">{t("Patients")}</p>
        <MultiSelect
          {...form.getCustomFieldProps("enduserIds")}
          options={endusers.map((enduser) => ({
            value: enduser.id,
            label: formatName(enduser) || t("Patient: {{id}}", { id: enduser.id }),
          }))}
          searchQuery={enduserSearch}
          setSearchQuery={setEnduserSearch}
          shouldFilter={false}
          clearSearchOnSelect={false}
          placeholder={t("Search patients...")}
          className="font-light w-full"
        />
      </div>
      <div className="h-[1px] bg-gray-200 w-full" />
      <Label>{t("Options")}</Label>
      <div className="flex gap-2 cursor-pointer">
        <Checkbox
          id="event-video-call"
          checked={form.values.enableVideoCall}
          onCheckedChange={(checked) => form.setFieldValue("enableVideoCall", !!checked)}
        />
        <Label htmlFor="event-video-call" className="font-light">
          {t("Include video call")}
        </Label>
      </div>
      <div className="flex gap-2 cursor-pointer">
        <Checkbox
          id="event-hidden-from-portal"
          checked={form.values.hiddenFromPortal}
          onCheckedChange={(checked) => form.setFieldValue("hiddenFromPortal", !!checked)}
        />
        <Label htmlFor="event-hidden-from-portal" className="font-light">
          {t("Hide from portal")}
        </Label>
      </div>
      <div className="flex gap-2 cursor-pointer">
        <Checkbox
          id="event-public-read"
          checked={form.values.publicRead}
          onCheckedChange={(checked) => form.setFieldValue("publicRead", !!checked)}
          disabled={!form.values.enableVideoCall}
        />
        <Label htmlFor="event-public-read" className="font-light">
          {t("Any patient can join this event")}
        </Label>
      </div>
      <div className="flex gap-2 cursor-pointer">
        <Checkbox
          id="event-dont-block-availability"
          checked={form.values.dontBlockAvailability}
          onCheckedChange={(checked) => form.setFieldValue("dontBlockAvailability", !!checked)}
        />
        <Label htmlFor="event-dont-block-availability" className="font-light">
          {t("Don't block availability")}
        </Label>
      </div>
    </form>
  );
};
